'use client'
import type { PrefetchKind } from 'next/dist/client/components/router-reducer/router-reducer-types'
import NextLink from 'next/link'
import { useParams, usePathname, useRouter } from 'next/navigation'
import { ComponentProps, useMemo } from 'react'

import { BASE_PATH } from '@/configs/const'

interface I18nNavigateOptions {
  locale?: string
  scroll?: boolean
}

/**
 * create path with locale
 * If have new locale, replace current locale with new locale
 * else use current locale
 */
function getPath({
  currentLocale,
  nextLocale,
  path,
}: {
  currentLocale: string
  nextLocale?: string
  path: string
}) {
  // if path do not have base path, add base path
  const newPath = path.includes(BASE_PATH) ? path : `${BASE_PATH}${path}`
  if (nextLocale) {
    const basePath = newPath.replace(`/${currentLocale}/`, '/')
    return `/${nextLocale}${basePath}`
  }
  return `/${currentLocale}${newPath}`
}

/**
 * would return path without locale
 */
export function useBasePath() {
  const params = useParams<{ locale: string }>()
  const pathname = usePathname()
  return pathname.replace(`/${params.locale}/`, '/')
}

/**
 * could not pass the base path '/price', would add base path automatically
 */
export function useI18nRouter() {
  const router = useRouter()
  const params = useParams<{ locale: string }>()
  return useMemo(() => {
    const replaceOrPush =
      (method: 'replace' | 'push') => (path: string, options?: I18nNavigateOptions) => {
        const nextPath = getPath({
          currentLocale: params.locale,
          nextLocale: options?.locale,
          path,
        })
        router[method](nextPath, { scroll: options?.scroll })
      }
    return {
      ...router,
      replace: replaceOrPush('replace'),
      push: replaceOrPush('push'),
      prefetch: (
        path: string,
        options?: {
          locale?: string
          kind?: PrefetchKind
          onInvalidate?: () => void
        },
      ) => {
        const nextPath = getPath({
          currentLocale: params.locale,
          nextLocale: options?.locale,
          path,
        })
        router.prefetch(
          nextPath,
          options?.kind ? { kind: options.kind, onInvalidate: options?.onInvalidate } : void 0,
        )
      },
    }
  }, [router, params.locale])
}

const REMOVE_LINK_STYLE = {
  textDecoration: 'none',
  color: 'inherit',
}

/**
 * href could not pass the base path '/price',would add base path automatically
 */
export function I18nLink({
  href,
  locale,
  children,
  ...otherProps
}: {
  href: string
  locale?: string
} & ComponentProps<typeof NextLink>) {
  const params = useParams<{ locale: string }>()
  const newHref = getPath({
    path: href,
    nextLocale: locale,
    currentLocale: params.locale,
  })
  return (
    <NextLink href={newHref} style={REMOVE_LINK_STYLE} {...otherProps}>
      {children}
    </NextLink>
  )
}
